import _ from "lodash";
import React, {
  cloneElement,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useHandleRightPane } from "Hooks";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import HelperPane from "IBPlanner/routes/PlannerStep/components/HelperPane";
import { goToRelativeRoute } from "modules/Services";
import { getRoleValue } from "Projects/config/constants";
import classes from "./ProjectGroup.scss";
import classNames from "classnames";
import {
  getProjectGroupProjectCountQuery,
  getProjectGroupStudentProjectPortfoliosCountQuery,
  getProjectGroupYearGroupQuery,
  getUserProjectGroupPortfoliosCountQuery,
  getUserProjectGroupYearGroupsQuery,
} from "Projects/modules/ProjectQueries";
import {
  getSelectedAcademicYear,
  getUserEntityType,
  getSelectedCurriculumId,
  getIsStaff,
} from "modules/CommonSelectors";
import {
  getProjectGroupProjectCountFromCache,
  getProjectGroupStudentProjectPortfoliosCountFromCache,
  getProjectGroupYearGroupFromCache,
  getUserProjectGroupPortfoliosCountFromCache,
} from "Projects/modules/ProjectGraphqlHelpers";
import withLoader from "UIComponents/GqlWithLoader";
import I18nHOC from "UIComponents/I18nHOC";
import { getPathFromFullPath, goToBack } from "modules/NavigationModule";
import { setActiveHomepageCard } from "Teacher/modules/TeacherModule";
import {
  getProjectGroupTypeFromId,
  updateYearGroupDetails,
  getHelpTextLabel,
  isIEAssessment,
  getUpdatedTemplateMemoize,
  updateRoleValue,
  resetRoleValue,
  getCurrentProjectGroupDetails,
  resetInsightsFilters,
  updateProjectModalAndDialogueValue,
  updateAcademicYearFilter,
} from "Projects/modules/ProjectModules";
import {
  getProjectGroupInternalSupervisorPendingApprovalCountQuery,
  getProjectGroupExternalSupervisorPendingApprovalCountQuery,
} from "Projects/routes/Setup/routes/Invite/modules/InviteQueries";
import {
  getProjectGroupInternalSupervisorPendingApprovalCountFromCache,
  getProjectGroupExternalSupervisorPendingApprovalCountFromCache,
} from "Projects/routes/Setup/routes/Invite/modules/InviteGraphqlHelpers";
import {
  PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
  ROLE_ADMIN,
  ROLE_SUPERVISOR,
  PROJECT_GROUP_ID_NOT_FOUND,
  PENDING_APPROVAL_SUPERVISOR_COUNT,
  RIGHT_PANE_ANIMATION_TIME,
  PROJECT_GROUP_CONFIGURATION_SETTINGS_NAME_MAP,
  PROJECT_GROUP_TYPE_DP_G4_PROJECT,
  PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT,
  DEFAULT_ARCHIVAL_STATE,
  PROJECT_GROUP_CATEGORY_TYPE_PORTFOLIO,
  PROJECT_GROUP_TYPE_DP_CAS,
  SAA_PORTFOLIO_REVAMP_PERM,
  PROJECT_GROUP_SUB_TYPE_INTERNAL_ASSESSMENT,
} from "Projects/Constants/stringConstants";
import {
  Button,
  Tabs,
  Badge,
  SideNavigation,
  PageHeader,
  EmptyState,
} from "@toddle-edu/ds-web";
import { NoServiceAsActionInsightsMobileIllustration } from "@toddle-edu/ds-theme";
import ArchivedYearGroupBanner from "Projects/components/ArchivedYearGroupBanner";
import AcademicYearFilter from "Projects/components/AcademicYearFilter";
import {
  InsightsSquareOutlined,
  OpenBookStarOutlined,
  DashboardOutlined,
  PortfolioOutlined,
  OpenBookTextOutlined,
  ScopeAndSequenceAddOutlined,
  HomeOutlined,
  CalendarScheduleOutlined,
  SetupOutlined,
  DeleteOutlined,
  OpenBookOutlined,
} from "@toddle-edu/ds-icons";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getUserDataBasedOnUserType,
  getIsProjectGroupTypePortfolio,
  getIsStudentNotMappedToProjectGroup,
  getDefaultProjectGroupConfigurationSettings,
  getIsDashboardsSideNavAdded,
  getProjectGroupCategoryIdByType,
} from "Projects/modules/Utils";
import { getOrganizationTemplatesWithHelpEnabledFieldsQuery } from "modules/CommonQuery";
import { getOrganizationTemplatesWithHelpEnabledFieldsFromCache } from "modules/CommonGraphqlHelpers";
import ACLStore from "lib/aclStore";
import { withCombinedAccessBlocker } from "lib/PermissionAware";
import { ROLE_VISITOR, STUDENT_USER } from "Constants/stringConstants";
import { getSettingValue } from "modules/PermissionModule";
import NotificationBadgesTooltip from "Projects/components/NotificationBadgesTooltip";
import { PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT } from "Projects/components/NotificationBadgesTooltip/modules/stringConstants";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import withControlParameters from "lib/aclService/hoc/withControlParameters";
import ACLStorage from "lib/aclService";
import { getProjectGroupContexts } from "Platform/modules/PlatformUtils";
import { CAN_VIEW_IA_EA_MODULE } from "Constants/permissionConstants";
import { isWebview as getIsWebView } from "Utils/SentralHelpers";
import { isClassModuleVisible } from "Teacher/routes/LearningCourse/modules/Utils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { WithConfigurationsAndPreferences } from "Utils/ConfigurationUtils/hoc";
import { generateConfigurationQueryVariablesV2 } from "Utils/ConfigurationUtils/Utils";

const TEST_ID_PREFIX = "projects-projectGroup";

const styles = {
  tabContainerStyle: {
    display: "flex",
    columnGap: "4px",
  },
  deadlineHeaderStyle: {
    containerStyle: {
      minHeight: 0,
      padding: "24px 0",
    },
    middleContainerStyle: {
      paddingLeft: "32px",
    },
    rightContainerStyle: {
      paddingRight: "32px",
    },
    topHeaderContainer: { padding: "0 16px", minHeight: "52px" },
  },
  newPageHeaderMiddleElementStyle: {
    marginRight: "52px",
  },
};

const getSidenavIcon = ({ badgeCount, showBadge }) => {
  return {
    OpenBookStarOutlined: <OpenBookStarOutlined />,
    DashboardOutlined: <DashboardOutlined />,
    PortfolioOutlined: <PortfolioOutlined />,
    InsightsSquareOutlined: <InsightsSquareOutlined />,
    OpenBookTextOutlined: <OpenBookTextOutlined />,
    OpenBookOutlined: <OpenBookOutlined />,
    SetupOutlined:
      showBadge && badgeCount > 0 ? (
        <NotificationBadgesTooltip
          notificationList={{
            [PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT]: badgeCount,
          }}
          tooltipPlacement="right"
        >
          <div className={classes.badgesWrapper}>
            <Badge value={badgeCount} size="xxx-small" dsVersion="2.0">
              <SetupOutlined size="xx-small" />
            </Badge>
          </div>
        </NotificationBadgesTooltip>
      ) : (
        <SetupOutlined size="xx-small" />
      ),
    ScopeAndSequenceAddOutlined: <ScopeAndSequenceAddOutlined />,
    HomeOutlined: <HomeOutlined />,
    CalendarScheduleOutlined: <CalendarScheduleOutlined />,
    DeleteOutlined: <DeleteOutlined />,
  };
};

const emptyObj = {};

const RightContainer = ({
  projectGroupType,
  onRightContainerClick,
  t,
  isUserStaff,
  currentPath,
}) => {
  switch (projectGroupType) {
    // REMOVE: this condition once SAA portfolio revamp is live for all
    case PROJECT_GROUP_TYPE_SERVICE_AS_ACTON: {
      if (ACLStore.can(SAA_PORTFOLIO_REVAMP_PERM)) {
        return !_.includes(["bin", "setup", "assessment"], currentPath) ? (
          <AcademicYearFilter />
        ) : null;
      }
      return isUserStaff ? (
        <Button
          icon={<DeleteOutlined />}
          type="outlined"
          variant="neutral"
          dsVersion="2.0"
          onClick={onRightContainerClick}
          testId={`${TEST_ID_PREFIX}-bin`}
        >
          {t("common:trash")}
        </Button>
      ) : null;
    }
    default:
      return null;
  }
};

const ProjectGroup = props => {
  const {
    goToBack,
    userTypeValue,
    projectGroupType,
    goToRelativeRoute,
    projectCount,
    portfolioCount,
    router: { location: { pathname = null } = {} } = {},
    t,
    params: { portfolioId },
    yearGroupDetails,
    updateYearGroupDetails,
    projectGroupId,
    template,
    projectGroupSubType,
    isFromClass,
    setActiveHomepageCard,
    projectGroupLabel,
    currentUserProjectsCount,
    currentUserPortfoliosCount,
    isUserTypeParent,
    childIdentifierName,
    pendingApprovalExternalSupervisorCount = 0,
    pendingApprovalInternalSupervisorCount = 0,
    userRole,
    isProjectGroupTypePortfolio,
    resetInsightsFilters,
    updateProjectModalAndDialogueValue,
    projectGroupName = "",
    courseName,
    yearGroupSwitchOptions = [],
    isUserTypeStudentOrParent,
    isUserStaff,
    updateAcademicYearFilter,
    isWebview,
    isIaEaAssessment,
  } = props;

  const isProjectGroupIEAssessment = isIEAssessment({
    projectGroupSubType,
  });

  const {
    currentRightPane,
    fieldId,
    showRightPane,
    onCloseRightPane,
    handleRightPane,
  } = useHandleRightPane({
    fieldId: "",
    currentRightPane: "",
    showRightPane: false,
    rightPaneAnimationTime: RIGHT_PANE_ANIMATION_TIME,
    shouldFocusHelpIconOnClose: true,
  });
  const mounted = useRef(false);

  useEffect(() => {
    const { resetRoleValue } = props;

    return () => {
      // portfolio insights filters
      resetInsightsFilters();
      resetRoleValue();
      updateAcademicYearFilter({});
      mounted.current = false;
    };
  }, [projectGroupId]);

  if (!mounted.current) {
    mounted.current = true;

    const { updateRoleValue, userTypeValue: role } = props;

    updateRoleValue(role);
  }

  const [currentFieldInfo, setCurrentFieldInfo] = useState({
    helperTextId: "",
    currentFieldKey: "",
  });

  const onHelpClick = params => {
    const { helperTextId, id, fieldKey } = params;
    handleRightPane({
      rightPane: "help",
      id,
      uid: fieldKey,
    });
    setCurrentFieldInfo(prevState => ({
      ...prevState,
      currentFieldKey: fieldKey,
      helperTextId,
    }));
  };

  const rightPaneClasses = classNames(
    { [classes.visible]: showRightPane },
    { [classes.hidden]: !showRightPane },
    { [classes.rightPane]: true }
  );

  const projectTitle = useMemo(() => {
    switch (projectGroupType) {
      case "SERVICE_AS_ACTION":
        return t("project:serviceAsAction_card");
      case "PERSONAL":
        return t("project:personal_project");
      case "COMMUNITY":
        return t("project:community_project");
      default:
        return "";
    }
  }, [projectGroupType]);

  const renderRightPaneComponent = currentRightPane => {
    switch (currentRightPane) {
      case "help":
        return (
          <HelperPane
            fieldKey={currentFieldInfo.currentFieldKey}
            helperTextId={currentFieldInfo.helperTextId}
            onClose={onCloseRightPane}
            label={getHelpTextLabel({
              template,
              uid: currentFieldInfo.currentFieldKey,
            })}
            testId="projects"
            dsVersion="2.0"
          />
        );
      default:
        return null;
    }
  };

  const projectGroupConfig = _.get(
    template,
    `body.projectGroupConfig.${isUserTypeParent ? "parent" : userTypeValue}`,
    emptyObj
  );

  const path = getPathFromFullPath(pathname);
  const currentPath = _.last(_.split(pathname, "/"));
  const isUserSupervisor = _.isEqual(ROLE_SUPERVISOR, userTypeValue);
  const isUserAdmin = _.isEqual(ROLE_ADMIN, userTypeValue);
  const isUserVisitor = _.isEqual(ROLE_VISITOR, userRole);

  const getBadgeCount = ({ badgeEntities }) => {
    let count = 0;
    _.forEach(badgeEntities, entity => {
      switch (entity) {
        case PENDING_APPROVAL_SUPERVISOR_COUNT:
          count =
            pendingApprovalExternalSupervisorCount +
            pendingApprovalInternalSupervisorCount;
          break;
        default:
          break;
      }
    });

    return count;
  };

  const tabValue = _.isEqual(path, userTypeValue) ? "guidance" : path;

  const toggleTab = value => {
    if (value === tabValue) {
      return;
    }

    if (currentRightPane) {
      onCloseRightPane();
    } else {
      goToRelativeRoute({
        route: `${projectGroupId}/${value}`,
        type: "replace",
        replacePath: projectGroupId,
      });
    }
  };

  const tabs = _.get(projectGroupConfig, "tabs", []);
  const sideBarFooterOptions = _.get(projectGroupConfig, "footerOptions", []);
  const tabsStringified = JSON.stringify(tabs);
  const footerOptionsStringified = JSON.stringify(sideBarFooterOptions);

  let updatedTabs = useMemo(() => {
    return _.map(tabs, tab => {
      const badgeCount = getBadgeCount({ badgeEntities: tab?.badgeEntities });

      return {
        label: isUserTypeParent
          ? t(tab.name, { label: childIdentifierName })
          : t(tab.name),
        value: tab?.path,
        suffix:
          tab?.showBadge && badgeCount > 0 ? (
            <NotificationBadgesTooltip
              notificationList={{
                [PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT]: badgeCount,
              }}
              tooltipPlacement="bottom"
            >
              <span>
                <Badge value={badgeCount} />
              </span>
            </NotificationBadgesTooltip>
          ) : null,
      };
    });
  }, [
    tabsStringified,
    isUserTypeParent,
    childIdentifierName,
    pendingApprovalExternalSupervisorCount,
    pendingApprovalInternalSupervisorCount,
  ]);

  const updatedFooterOptions = useMemo(() => {
    return _.map(sideBarFooterOptions, option => {
      const { name, path, icon } = option;
      return {
        key: path,
        title: t(name),
        tier: 1,
        prefix: getSidenavIcon({})[icon],
        parent: "root",
      };
    });
  }, [t, footerOptionsStringified]);

  //When a user is not Admin
  // CASE 1 - user is a internal supervisor for a project but is not a advisor for any portfolio
  //   ----------> Showing only experiences tab
  //CASE 2 - user is an advisor for a portfolio but is not a supervisor for any project
  //   ----------> Removing experiences tab, and showing the rest

  const isDashboardsSideNavAdded = getIsDashboardsSideNavAdded({
    projectGroupType,
    projectGroupSubType,
    userTypeValue,
  });

  const sideNavRootOption = {
    key: "root",
    children: _.map(tabs, tab => tab?.path),
  };

  const sideNavFooterRootOption = {
    key: "root",
    children: _.map(sideBarFooterOptions, option => option?.path),
  };

  let sideNavChildrenOptions = _.map(tabs, tab => {
    const {
      name,
      path,
      isDivider = false,
      showBadge = false,
      badgeEntities = false,
      icon,
    } = tab;
    const badgeCount = getBadgeCount({ badgeEntities });

    return {
      title: !isDivider
        ? isUserTypeParent
          ? t(name, { label: childIdentifierName })
          : t(name)
        : null,
      key: path,
      isDivider,
      suffix:
        showBadge && badgeCount > 0 && !isDivider ? (
          <NotificationBadgesTooltip
            notificationList={{
              [PENDING_APPROVAL_NOTIFICATION_BADGES_COUNT]: badgeCount,
            }}
            tooltipPlacement="bottom"
          >
            <span>
              <Badge value={badgeCount} />
            </span>
          </NotificationBadgesTooltip>
        ) : null,
      prefix: !isDivider
        ? getSidenavIcon({ badgeCount, showBadge })[icon]
        : null,
      tier: 1,
      parent: "root",
    };
  });

  if (isProjectGroupTypePortfolio) {
    if (
      (isUserSupervisor &&
        currentUserPortfoliosCount == 0 &&
        currentUserProjectsCount != 0) ||
      isUserVisitor
    ) {
      updatedTabs = _.filter(updatedTabs, tab => tab.value == "experiences");

      const includePathNames = ["experiences"];
      sideNavRootOption["children"] = _.filter(
        sideNavRootOption["children"],
        item => _.includes(includePathNames, item)
      );
      sideNavChildrenOptions = _.filter(sideNavChildrenOptions, option => {
        return _.includes(includePathNames, option.key);
      });
    }
  }

  if (!isDashboardsSideNavAdded) {
    updatedTabs = _.filter(updatedTabs, tab => "divider" !== tab.value);
  }

  const sideNavOptions = [sideNavRootOption, ...sideNavChildrenOptions];
  const sideNavFooterOptions = [
    sideNavFooterRootOption,
    ...updatedFooterOptions,
  ];

  const changeYearGroup = ({ key }) => {
    const route =
      isUserSupervisor && currentPath === "supervisor"
        ? `${key}/guidance/supervisor`
        : `${key}/${currentPath}`;

    goToRelativeRoute({
      route,
      type: "replace",
      replacePath: projectGroupId,
    });
    resetInsightsFilters();
  };

  const dropdownMenuProps = {
    options: yearGroupSwitchOptions,
    onClick: changeYearGroup,
    value: _.find(yearGroupSwitchOptions, { key: projectGroupId })?.key,
    showSelection: true,
  };

  const onRightContainerClick = () => {
    switch (projectGroupType) {
      // REMOVE: this condition once SAA portfolio revamp is live for all
      case PROJECT_GROUP_TYPE_SERVICE_AS_ACTON: {
        goToRelativeRoute({
          route: `${projectGroupId}/bin`,
          type: "replace",
          replacePath: projectGroupId,
        });
        break;
      }
      case PROJECT_GROUP_TYPE_DP_G4_PROJECT:
      case PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT: {
        //create modal is getting rendered by src/routes/routes/Teacher/routes/Projects/components/ProjectFeedlistContainer/ProjectFeedlistContainer.js
        updateProjectModalAndDialogueValue({
          showCreateProjectModal: true,
        });
        break;
      }
      default:
        return null;
    }
  };

  const getDefaultRouteValue = () => {
    if (isUserAdmin) {
      return _.get(tabs, "0.path", "setup");
    }
    if (isProjectGroupTypePortfolio) {
      return "portfolio";
    } else {
      return "progress";
    }
  };

  const goToDefaultProjectPage = () => {
    const defaultRoute = _.get(
      projectGroupConfig,
      "defaultRoute",
      getDefaultRouteValue()
    );

    const projectsCreated = isProjectGroupTypePortfolio
      ? portfolioCount > 0
      : projectCount > 0;

    if (
      (isUserAdmin || (isUserSupervisor && isProjectGroupIEAssessment)) &&
      !projectsCreated
    ) {
      const defaultPath = isUserSupervisor ? "overview" : "setup";

      const path = _.get(tabs, `[0].path`, defaultPath);

      goToRelativeRoute({
        route: `./${path}`,
        type: "replace",
      });
    } else if (
      (isProjectGroupTypePortfolio &&
        isUserSupervisor &&
        currentUserPortfoliosCount == 0 &&
        currentUserProjectsCount != 0) ||
      isUserVisitor
    ) {
      const path = "experiences";
      goToRelativeRoute({
        route: `./${path}`,
        type: "replace",
      });
    } else if (
      isUserSupervisor &&
      (isProjectGroupTypePortfolio
        ? currentUserPortfoliosCount == 0 && currentUserProjectsCount == 0
        : !projectsCreated &&
          // REMOVE: this condition once SAA portfolio revamp is live for all
          projectGroupType !== PROJECT_GROUP_TYPE_SERVICE_AS_ACTON)
    ) {
      const path = _.first(tabs)?.path;
      goToRelativeRoute({
        route: `./${path}`,
        type: "replace",
      });
    } else {
      goToRelativeRoute({ route: `./${defaultRoute}`, type: "replace" });
    }
  };

  useEffect(() => {
    if (isFromClass) {
      setActiveHomepageCard({
        from: "courses",
        label: projectGroupLabel,
        isLocalized: true,
      });
    } else {
      setActiveHomepageCard({
        from: "home-page",
        label: projectTitle,
        isLocalized: true,
      });
    }
    if (!props.children && !_.isEmpty(projectGroupConfig)) {
      goToDefaultProjectPage();
    }
  }, [props.children, projectGroupConfig, projectGroupType]);

  useEffect(() => {
    const { key } = _.first(sideNavChildrenOptions) || {};

    if (
      !_.find(sideNavChildrenOptions, { key: currentPath }) &&
      key !== currentPath &&
      _.includes(
        ["portfolios", "portfolioInsights", "supervisor"],
        currentPath
      ) &&
      userTypeValue === ROLE_SUPERVISOR
    ) {
      goToRelativeRoute({
        route: `${projectGroupId}/${key}`,
        type: "replace",
        replacePath: projectGroupId,
      });
    }

    updateYearGroupDetails(yearGroupDetails);
  }, [projectGroupId]);

  const { isArchived } = yearGroupDetails;

  //This condition is added for guidance routes which have there trailing path as user type.
  if (props.children) {
    const childrenComponent = cloneElement(props.children, {
      showHeader: false,
      template,
      projectGroupSubType,
      projectGroupType,
      onCloseRightPane,
      onHelpClick,
      currentRightPane,
      fieldId,
      currentFieldInfo,
      pendingApprovalInternalSupervisorCount,
      pendingApprovalExternalSupervisorCount,
      headerStyle: styles.deadlineHeaderStyle,
      yearGroupDetails,
    });

    const middleContainer =
      _.size(updatedTabs) > 1 ? (
        <Tabs
          name="project-group-tabs"
          onChange={toggleTab}
          options={updatedTabs}
          value={tabValue}
          containerStyle={styles.tabContainerStyle}
          dsVersion="2.0"
          testId={TEST_ID_PREFIX}
        />
      ) : null;

    const rightContainer = (
      <RightContainer
        projectGroupType={projectGroupType}
        onRightContainerClick={onRightContainerClick}
        t={t}
        isUserStaff={isUserStaff}
        currentPath={currentPath}
      />
    );

    const getBodyContent = () => {
      if (isWebview) {
        return (
          <EmptyState
            illustration={NoServiceAsActionInsightsMobileIllustration}
            title={t("project:web_view_disabled_state_title")}
            subtitle={t("project:web_view_disabled_state_subtext_with_label", {
              label: isProjectGroupTypePortfolio
                ? t("project:portfolio")
                : isIaEaAssessment
                ? t("common:assessment")
                : t("project:project"),
            })}
            dsVersion="2.0"
            testId={`${TEST_ID_PREFIX}-webview-empty-state`}
          ></EmptyState>
        );
      } else {
        return isDashboardsSideNavAdded ? (
          <div className={classes.sideNavAndChildWrapper}>
            <div className={classes.sideNav}>
              <SideNavigation
                dsVersion="2.0"
                isCollapsible={true}
                options={sideNavOptions}
                footerOptions={sideNavFooterOptions}
                selectedOptionKey={tabValue}
                onClick={toggleTab}
                areShortcutsEnabled={true}
                testId={TEST_ID_PREFIX}
              />
            </div>
            <div className={classes.childrenWrapper}>{childrenComponent}</div>
          </div>
        ) : (
          childrenComponent
        );
      }
    };

    return (
      <div className={classes.pageContainer}>
        {/* todo : update the tablist component where the hover effect can be applied */}
        {/* We need to hide the top header in case of CAS student portfolios in all the sides(student,advisor,coordinator) as we are creating a dynamic header for that page */}
        {/* we need to hide top header in CAS projectApproval screen in coordinator side */}
        {isArchived && <ArchivedYearGroupBanner />}
        {!(
          _.includes(["studentPortfolio", portfolioId], currentPath) &&
          isProjectGroupTypePortfolio
        ) ? (
          <div>
            <PageHeader dsVersion="2.0">
              <PageHeader.Left
                title={projectGroupName}
                subtitle={isProjectGroupIEAssessment ? courseName : null}
                onBackClick={!isProjectGroupIEAssessment ? goToBack : null}
                dropdownMenuProps={
                  isProjectGroupIEAssessment ||
                  isUserTypeStudentOrParent ||
                  isWebview
                    ? null
                    : dropdownMenuProps
                }
                testId={TEST_ID_PREFIX}
              />
              {!isDashboardsSideNavAdded && !isWebview ? (
                <PageHeader.Middle
                  style={
                    isProjectGroupIEAssessment ||
                    // REMOVE: this condition once SAA portfolio revamp is live for all
                    projectGroupType === PROJECT_GROUP_TYPE_SERVICE_AS_ACTON
                      ? styles.newPageHeaderMiddleElementStyle
                      : {}
                  }
                >
                  {middleContainer}
                </PageHeader.Middle>
              ) : null}
              {(!isDashboardsSideNavAdded && !isWebview) ||
              (projectGroupType === PROJECT_GROUP_TYPE_SERVICE_AS_ACTON &&
                ACLStore.can(SAA_PORTFOLIO_REVAMP_PERM) &&
                !isWebview) ? (
                <PageHeader.Right>{rightContainer}</PageHeader.Right>
              ) : null}
            </PageHeader>
          </div>
        ) : null}

        {getBodyContent()}

        <div className={rightPaneClasses}>
          {renderRightPaneComponent(currentRightPane)}
        </div>
      </div>
    );
  }

  return null;
};

ProjectGroup.displayName = "ProjectGroup";

const mapActionCreators = {
  goToRelativeRoute,
  goToBack,
  updateYearGroupDetails,
  setActiveHomepageCard,
  updateRoleValue,
  resetRoleValue,
  resetInsightsFilters,
  updateProjectModalAndDialogueValue,
  updateAcademicYearFilter,
};

const mapStateToProps = (state, ownProps) => {
  const isUserStaff = getIsStaff(state);
  const userInfo = getUserInfo();

  const { user_type, org_id: organizationId, role: userRole } = userInfo;

  const projectGroupDetails = getCurrentProjectGroupDetails({ state });
  const {
    type: projectGroupType,
    id: projectGroupId,
    category: projectGroupCategoryType,
    subType: projectGroupSubType = "",
    globalProjectGroupId,
  } = projectGroupDetails;

  const userData = getUserDataBasedOnUserType();
  const { t } = ownProps;

  const currentCurriculumProgramId = getSelectedCurriculumId(state);
  const { course_id: courseId, learningCourseId } = ownProps.params;
  const userId = userData.userId;

  const projectGroupLabel = _.get(projectGroupDetails, "name", "");

  const curriculumProgramIds = [currentCurriculumProgramId];

  const currentCurriculumProgramType = _.find(
    _.get(state, "platform.organizationCurriculumPrograms", []),
    { id: currentCurriculumProgramId }
  )?.type;
  const isProjectGroupTypePortfolio = getIsProjectGroupTypePortfolio({
    projectGroupType,
    projectGroupCategoryType,
  });

  const projectGroupCategoryIdByType = getProjectGroupCategoryIdByType({
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    organizationId,
    curriculumType: currentCurriculumProgramType,
  });

  const projectGroupCategoryId = projectGroupCategoryIdByType[projectGroupType];

  const templateFilters = {
    templateType: isProjectGroupTypePortfolio ? "PORTFOLIO" : "PROJECT",
    curriculumIds: currentCurriculumProgramType
      ? [currentCurriculumProgramType]
      : [],
    subTypes: projectGroupType ? [projectGroupType] : [],
    globalProjectGroupIds: globalProjectGroupId ? [globalProjectGroupId] : [],
  };
  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    null
  );

  // logic to decide whether user has access to the project group or not for non IAs and EAs
  const isIaEaAssessment = isIEAssessment({
    projectGroupSubType,
  });

  //check if ia/ea module access is blocked through course settings (rnp)
  const isModuleVisibleInSidebar = isClassModuleVisible({
    permission: CAN_VIEW_IA_EA_MODULE,
    classId: courseId,
    learningCourseId,
  });

  // if user is not mapped to project group or project group is not found or student is not mapped to project group
  // or user is not allowed to view IA/EA module then access is blocked
  // Note: projectGroupType from Apollo (state.project.projectGroupDetails) takes precedence over stale
  // login.userInfo.projectGroups — fixes access blocked on first nav after a new project module is created
  const isProjectGroupOrAssessmentAccessBlocked = isIaEaAssessment
    ? !ACLStorage.getModuleSettingsValue(CAN_VIEW_IA_EA_MODULE)
    : !projectGroupType &&
      _.isEqual(
        getProjectGroupTypeFromId({
          state,
          projectGroupId,
        }),
        PROJECT_GROUP_ID_NOT_FOUND
      );

  const isStudentNotMappedToProjectGroup = getIsStudentNotMappedToProjectGroup({
    state,
    userType: user_type,
  });

  const isAccessBlocked =
    isProjectGroupOrAssessmentAccessBlocked || isStudentNotMappedToProjectGroup;

  let errorText = t("common:sorry_you_dont_have_access_to_with_module_name", {
    moduleName: t("project:this_project_group"),
  });
  let errorSubText = t("common:contact_school_admin");
  //updated blocked access info if ia/ea module access is blocked through course settings (rnp)
  if (isIaEaAssessment && !isModuleVisibleInSidebar) {
    errorText = t("project:no_longer_access_with_module_name", {
      moduleName:
        projectGroupSubType === PROJECT_GROUP_SUB_TYPE_INTERNAL_ASSESSMENT
          ? t("project:internal_assessment_lowercase")
          : t("project:external_assessment_lowercase"),
    });
    errorSubText = t("project:get_in_touch_school_admin");
  }

  const academicYearId = _.get(state, "platform.academicYearSelected.id", "");

  const academicYearSelected = getSelectedAcademicYear(state);

  const { isCurrentAcademicYear = true } = academicYearSelected;

  const showArchivedStudents = ACLStore.can(
    "FeatureFlag:ShowArchiveStudentInNonCurrentAY"
  );

  const userEntityType = getUserEntityType();

  const projectGroupConfigurationSettingsMap = getSettingValue({
    name: PROJECT_GROUP_CONFIGURATION_SETTINGS_NAME_MAP[projectGroupType],
    userEntityType,
    userId,
    currentCurriculumProgramId,
  });

  const defaultProjectGroupConfigurationSettings = getDefaultProjectGroupConfigurationSettings(
    {
      projectGroupType,
    }
  );

  const currentProjectGroupConfigurationSettings = _.get(
    projectGroupConfigurationSettingsMap,
    projectGroupId,
    defaultProjectGroupConfigurationSettings
  );

  const courseGradeFilters = {
    academicYearId,
  };

  const courseName = courseId
    ? _.get(
        getCourseDetailsFromCache({ id: courseId, courseGradeFilters }),
        "title",
        ""
      )
    : "";

  const yearGroupCurriculumProgramFilter = !ACLStore.can(
    "FeatureFlag:ShowAllYearGroupProjectGroups"
  )
    ? {
        yearGroupCurriculumProgramIds: currentCurriculumProgramId
          ? [currentCurriculumProgramId]
          : [],
      }
    : {};

  const isUserTypeParent = userData.isUserTypeParent;

  const isUserTypeStudent = _.isEqual(userData.userType, STUDENT_USER);

  const isUserTypeStudentOrParent = isUserTypeParent || isUserTypeStudent;

  // if user is parent or student we need default user type as query will not called for them
  const userTypeValue = getRoleValue({
    user_type,
    projectGroupCategoryId,
    curriculumProgramId: currentCurriculumProgramId,
  });
  const isWebview = getIsWebView();

  return {
    projectGroupId,
    isGuidanceVisible: false,
    projectGroupType,
    userId,
    curriculumProgramIds,
    courseId,
    organizationId,
    templateFilters,
    courses: courseId ? [courseId] : [],
    isFromClass: !_.isEmpty(selectedCourseId),
    projectGroupLabel,
    isAccessBlocked,
    errorText,
    errorSubText,
    isUserTypeParent,
    childIdentifierName: userData.childPreferredName || userData.childFirstName,
    academicYearId,
    userRole,
    studentArchivalState:
      showArchivedStudents && !isCurrentAcademicYear ? "ALL" : undefined,
    currentProjectGroupConfigurationSettings,
    isData: true,
    isLoading: false,
    isProjectGroupTypePortfolio,
    isUserTypeStudent,
    courseName,
    yearGroupCurriculumProgramFilter,
    currentCurriculumProgramId,
    userEntityType,
    isUserTypeStudentOrParent,
    isUserStaff,
    projectGroupCategoryType,
    projectGroupCategoryId,
    user_type,
    userTypeValue,
    isWebview,
    isIaEaAssessment,
    learningCourseId,
    isLearningCourseEnabled: getIsLearningCoursesEnabled(),
  };
};

export default compose(
  I18nHOC({
    resource: ["project", "common", "projectTemplate"],
  }),
  connect(mapStateToProps, mapActionCreators),
  withCombinedAccessBlocker,
  withControlParameters({
    skip: ({ projectGroupCategoryId }) => !projectGroupCategoryId,
    options: ({ projectGroupCategoryId, currentCurriculumProgramId }) => {
      return {
        variables: {
          filters: {
            module: "PROJECTS",
            appType: ["TEACHER_WEB"],
            contexts: getProjectGroupContexts({
              projectGroupCategoryId,
              curriculumProgramId: currentCurriculumProgramId,
            }),
          },
        },
      };
    },
    getControlParameterBasedProps: ({ ownProps }) => {
      const {
        projectGroupCategoryId,
        user_type,
        currentCurriculumProgramId,
      } = ownProps;
      // we will have to overwrite the role if rnp is true.
      const userTypeValue = getRoleValue({
        user_type,
        projectGroupCategoryId,
        curriculumProgramId: currentCurriculumProgramId,
      });
      return {
        userTypeValue,
      };
    },
  }),

  graphql(getProjectGroupYearGroupQuery, {
    name: "getProjectGroupYearGroup",
    options: ({ projectGroupId, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: projectGroupId,
        gradeFilters: {
          academicYearId,
        },
      },
    }),
    props({
      getProjectGroupYearGroup,
      ownProps: { isData, isLoading, projectGroupId, academicYearId },
    }) {
      const data = getProjectGroupYearGroupFromCache({
        id: projectGroupId,
        gradeFilters: {
          academicYearId,
        },
      });
      const projectGroupSubType = _.get(data, "node.subType", "");

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getProjectGroupYearGroup["networkStatus"]) ||
          isLoading,
        yearGroupDetails: _.get(data, "node.yearGroup", emptyObj),
        projectGroupSubType,
        projectGroupName: _.get(data, "node.name", ""),
      };
    },
  }),
  withControlParameters({
    skip: ({ projectGroupSubType }) =>
      !isIEAssessment({
        projectGroupSubType,
      }),
    options: ({ currentCurriculumProgramId, courseId }) => ({
      variables: {
        filters: {
          module: "IAEA",
          appType: ["TEACHER_WEB"],
          contexts: [
            [
              {
                entityType: "CURRICULUM_PROGRAM",
                entityId: currentCurriculumProgramId,
              },
              {
                entityType: "COURSE",
                entityId: courseId,
              },
            ],
          ],
        },
      },
    }),
  }),

  graphql(getOrganizationTemplatesWithHelpEnabledFieldsQuery, {
    name: "getOrganizationTemplates",
    options: ({ organizationId, templateFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: templateFilters,
      },
    }),
    props: ({
      getOrganizationTemplates,
      ownProps: {
        organizationId,
        templateFilters,
        isData,
        isLoading,
        userTypeValue,
        currentProjectGroupConfigurationSettings,
        t,
      },
    }) => {
      const data = getOrganizationTemplatesWithHelpEnabledFieldsFromCache({
        id: organizationId,
        filters: templateFilters,
      });

      const template = _.first(_.get(data, "node.templates", []));

      // For local development
      // let template = {};
      // const templateId = _.first(_.get(data, "node.templates", []))?.id;
      // if (templateId) {
      //   template = require(`templates/ProjectTemplates/${templateId}.json`);
      // }

      let updatedTemplate = {};
      if (!_.isEmpty(template)) {
        updatedTemplate = getUpdatedTemplateMemoize({
          template,
          skipLocalisation: false,
          role: userTypeValue,
          templatePropertiesToUpdate: [
            "columnList",
            "projectGroupConfig",
            "actionsList",
            "nodes",
          ],
          updateTableConfig: true,
          keepConditionalConfiguration: true,
          currentProjectGroupConfigurationSettings,
          t,
        });
      }

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationTemplates["networkStatus"]) ||
          isLoading,
        template: updatedTemplate,
      };
    },
  }),
  graphql(getProjectGroupProjectCountQuery, {
    name: "getProjectGroupProjectCount",
    skip: ({ userTypeValue, isProjectGroupTypePortfolio }) =>
      !(
        (ROLE_ADMIN === userTypeValue && !isProjectGroupTypePortfolio) ||
        _.isEqual(ROLE_SUPERVISOR, userTypeValue)
      ),
    options: ({
      projectGroupId,
      courses,
      studentArchivalState,
      academicYearId,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: projectGroupId,
          filters: {
            courses,
            studentArchivalState,
            academicYearIds: _.compact([academicYearId]),
          },
        },
      };
    },
    props({ getProjectGroupProjectCount, ownProps: { isLoading, isData } }) {
      const variables = getProjectGroupProjectCount.variables;

      const result = getProjectGroupProjectCountFromCache(variables);
      const projectCount = _.get(result, "node.projects.totalCount", "");
      return {
        projectCount,
        isData: !_.isEmpty(result) && isData,
        isLoading:
          getProjectGroupProjectCount["networkStatus"] == 1 ||
          getProjectGroupProjectCount["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getProjectGroupStudentProjectPortfoliosCountQuery, {
    name: "getProjectGroupStudentProjectPortfoliosCount",
    skip: ({ userTypeValue, isProjectGroupTypePortfolio }) =>
      !(ROLE_ADMIN === userTypeValue && isProjectGroupTypePortfolio),
    options: ({ projectGroupId, studentArchivalState, academicYearId }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: projectGroupId,
          portfolioFilters: {
            ...(studentArchivalState ? { studentArchivalState } : {}),
            academicYearIds: _.compact([academicYearId]),
          },
        },
      };
    },
    props({
      getProjectGroupStudentProjectPortfoliosCount,
      ownProps: { isLoading, isData },
    }) {
      const variables = _.get(
        getProjectGroupStudentProjectPortfoliosCount,
        "variables",
        {}
      );
      const result = getProjectGroupStudentProjectPortfoliosCountFromCache(
        variables
      );

      const portfolioCount = _.get(
        result,
        "node.studentProjectPortfolio.totalCount",
        ""
      );
      return {
        portfolioCount,
        isData: !_.isEmpty(result) && isData,
        isLoading:
          _.includes(
            [1, 2],
            getProjectGroupStudentProjectPortfoliosCount["networkStatus"]
          ) || isLoading,
      };
    },
  }),
  graphql(getUserProjectGroupPortfoliosCountQuery, {
    name: "getUserProjectGroupPortfoliosCount",
    skip: ({ isProjectGroupTypePortfolio, isUserTypeStudent }) =>
      !isProjectGroupTypePortfolio || isUserTypeStudent,
    options: ({
      params: { projectGroup },
      userId,
      studentArchivalState,
      academicYearId,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: projectGroup,
          portfolioFilters: {
            advisers: [userId],
            ...(studentArchivalState ? { studentArchivalState } : {}),
            academicYearIds: _.compact([academicYearId]),
          },
        },
      };
    },
    props({
      getUserProjectGroupPortfoliosCount,
      ownProps: { isData, isLoading },
    }) {
      const variables = _.get(
        getUserProjectGroupPortfoliosCount,
        "variables",
        {}
      );
      const result = getUserProjectGroupPortfoliosCountFromCache(variables);

      const currentUserPortfoliosCount = _.get(
        result,
        "node.studentProjectPortfolio.totalCount",
        ""
      );

      return {
        currentUserPortfoliosCount,
        isData: !_.isEmpty(result) && isData,
        isLoading:
          getUserProjectGroupPortfoliosCount["networkStatus"] == 1 ||
          getUserProjectGroupPortfoliosCount["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getProjectGroupProjectCountQuery, {
    name: "getProjectGroupProjectCount",
    skip: ({ isProjectGroupTypePortfolio, isUserTypeStudent }) =>
      !isProjectGroupTypePortfolio || isUserTypeStudent,
    options: ({
      params: { projectGroup },
      userId,
      studentArchivalState,
      academicYearId,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: projectGroup,
          filters: {
            supervisors: [userId],
            studentArchivalState,
            academicYearIds: _.compact([academicYearId]),
          },
        },
      };
    },
    props({ getProjectGroupProjectCount, ownProps: { isData, isLoading } }) {
      const variables = getProjectGroupProjectCount.variables;
      const result = getProjectGroupProjectCountFromCache(variables);
      const currentUserProjectsCount = _.get(
        result,
        "node.projects.totalCount",
        ""
      );

      return {
        currentUserProjectsCount,
        isData: !_.isEmpty(result) && isData,
        isLoading:
          getProjectGroupProjectCount["networkStatus"] == 1 ||
          getProjectGroupProjectCount["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),

  graphql(getProjectGroupInternalSupervisorPendingApprovalCountQuery, {
    name: "getProjectGroupInternalSupervisorPendingApprovalCount",
    skip: ({ isUserTypeStudent, projectGroupCategoryType }) => {
      return (
        isUserTypeStudent ||
        PROJECT_GROUP_CATEGORY_TYPE_PORTFOLIO !== projectGroupCategoryType
      );
    },
    options: ({ projectGroupId, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        projectGroupId,
        academicYearIds: _.compact([academicYearId]),
      },
    }),
    props({
      getProjectGroupInternalSupervisorPendingApprovalCount,
      ownProps: { isData, isLoading },
    }) {
      const data = getProjectGroupInternalSupervisorPendingApprovalCountFromCache(
        _.get(
          getProjectGroupInternalSupervisorPendingApprovalCount,
          "variables",
          {}
        )
      );

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getProjectGroupInternalSupervisorPendingApprovalCount[
            "networkStatus"
          ] == 1 ||
          getProjectGroupInternalSupervisorPendingApprovalCount[
            "networkStatus"
          ] == 2 ||
          isLoading,
        pendingApprovalInternalSupervisorCount: _.get(
          data,
          "node.supervisors.totalCount",
          0
        ),
      };
    },
  }),
  graphql(getProjectGroupExternalSupervisorPendingApprovalCountQuery, {
    name: "getProjectGroupExternalSupervisorPendingApprovalCount",
    skip: ({ isUserTypeStudent, projectGroupType }) => {
      return (
        (isUserTypeStudent || projectGroupType !== PROJECT_GROUP_TYPE_DP_CAS) &&
        ACLStore.can("FeatureFlag:CASExternalSupervisor")
      );
    },
    options: ({ projectGroupId, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        projectGroupId,
        academicYearIds: _.compact([academicYearId]),
      },
    }),
    props({
      getProjectGroupExternalSupervisorPendingApprovalCount,
      ownProps: { isData, isLoading },
    }) {
      const data = getProjectGroupExternalSupervisorPendingApprovalCountFromCache(
        _.get(
          getProjectGroupExternalSupervisorPendingApprovalCount,
          "variables",
          {}
        )
      );

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getProjectGroupExternalSupervisorPendingApprovalCount[
            "networkStatus"
          ] == 1 ||
          getProjectGroupExternalSupervisorPendingApprovalCount[
            "networkStatus"
          ] == 2 ||
          isLoading,
        pendingApprovalExternalSupervisorCount: _.get(
          data,
          "node.supervisors.totalCount",
          0
        ),
      };
    },
  }),
  graphql(getUserProjectGroupYearGroupsQuery, {
    name: "getUserProjectGroupYearGroups",
    skip: ({ isUserTypeStudentOrParent, projectGroupSubType }) => {
      return (
        isUserTypeStudentOrParent ||
        isIEAssessment({
          projectGroupSubType,
        })
      );
    },
    options: ({
      userId,
      userEntityType,
      projectGroupType,
      currentCurriculumProgramId,
      yearGroupCurriculumProgramFilter,
      academicYearId,
      isUserTypeParent,
      studentArchivalState,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
        filters: {
          projectGroupTypes: projectGroupType ? [projectGroupType] : [],
          yearGroupSearchText: "",
          yearGroupArchivalState: DEFAULT_ARCHIVAL_STATE,
          ...yearGroupCurriculumProgramFilter,
          curriculumProgramIds: currentCurriculumProgramId
            ? [currentCurriculumProgramId]
            : [],
          academicYears: _.compact([academicYearId]),
        },
        gradeFilters: {
          academicYearId,
        },
        projectFilters: {
          students: isUserTypeParent ? [userId] : [],
          studentArchivalState,
          academicYearIds: _.compact([academicYearId]),
        },
      },
    }),
    props({
      getUserProjectGroupYearGroups,
      ownProps: { isData, isLoading, t },
    }) {
      const yearGroupSwitchOptions = _.chain(getUserProjectGroupYearGroups)
        .get("node.projectGroups", [])
        .filter(item => {
          const { isArchived } = item.yearGroup;
          return !(
            !ACLStore.can("FeatureFlag:EnableArchiveYGProjects") && isArchived
          );
        })
        .orderBy(["yearGroup.isArchived"], ["asc"])
        .map(item => {
          const {
            yearGroup: { name, isArchived },
            id,
          } = item;
          return {
            key: id,
            label: name,
            subText: isArchived ? t("common:archived") : "",
          };
        })
        .value();

      return {
        isData: !_.isEmpty(getUserProjectGroupYearGroups.node) && isData,
        isLoading:
          _.includes([1, 2], getUserProjectGroupYearGroups["networkStatus"]) ||
          isLoading,
        yearGroupSwitchOptions,
      };
    },
  }),

  WithConfigurationsAndPreferences({
    skip: ({ organizationId, currentCurriculumProgramId }) =>
      !organizationId || !currentCurriculumProgramId,
    options: ({ organizationId, currentCurriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "ORG_CURRICULUM_LEVEL",
        organizationId,
        currentCurriculumProgramId,
        modules: ["PROJECTS"],
      }),
    }),
    getConfigurationKeys: () => ["ORGANIZATION", "CURRICULUM"],
  }),
  WithConfigurationsAndPreferences({
    skip: ({ courseId }) => !courseId,
    options: ({ courseId }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "COURSE_LEVEL",
        courseId,
        modules: ["PROJECTS"],
      }),
    }),
    getConfigurationKeys: () => ["COURSE"],
  }),
  WithConfigurationsAndPreferences({
    skip: ({ learningCourseId, isLearningCourseEnabled }) =>
      !isLearningCourseEnabled || !learningCourseId,
    options: ({ learningCourseId }) => ({
      fetchPolicy: "cache-and-network",
      variables: generateConfigurationQueryVariablesV2({
        src: "LEARNING_COURSE_LEVEL",
        learningCourseId,
        modules: ["PROJECTS"],
      }),
    }),
    getConfigurationKeys: () => ["LEARNING_COURSE"],
  }),
  withLoader
)(ProjectGroup);
